import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { BarChart3, Loader2, Cog, AlertTriangle, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

const EquipamentosPainelPage = () => {
  const { getEmpresaId, hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [utilizacao, setUtilizacao] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const empresa_id = getEmpresaId();
        const [rDash, rUtil] = await Promise.all([
          httpsCallable(functions, 'getEquipamentosDashboard')({ empresa_id }),
          httpsCallable(functions, 'getUtilizacaoEquipamentos')({ empresa_id, limit: 20 }),
        ]);
        if (!cancelled) {
          setData(rDash.data?.data);
          setUtilizacao(rUtil.data?.data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getEmpresaId]);

  if (!hasPermission('view_equipment_dashboard')) {
    return <div className="p-6 text-center text-gray-600">Sem permissão.</div>;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
      </div>
    );
  }

  const t = data?.totais || {};
  const m = data?.manutencao || {};

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="h-7 w-7 text-blue-600" />
          Painel de Equipamentos
        </h1>
        <p className="text-sm text-gray-600 mt-1">Visão geral de ativos, manutenção e obsolescência.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Ativos', value: t.ativos, color: 'text-green-700 bg-green-50' },
          { label: 'Em manutenção', value: t.em_manutencao, color: 'text-amber-700 bg-amber-50' },
          { label: 'Parados', value: t.parados, color: 'text-gray-700 bg-gray-50' },
          { label: 'Total', value: t.total, color: 'text-blue-700 bg-blue-50' },
        ].map((c) => (
          <div key={c.label} className={`rounded-xl border p-4 ${c.color}`}>
            <p className="text-xs uppercase font-medium opacity-80">{c.label}</p>
            <p className="text-3xl font-bold mt-1">{c.value ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl p-5">
          <h2 className="font-semibold flex items-center gap-2 text-gray-900">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Manutenção
          </h2>
          <p className="mt-3 text-2xl font-bold text-red-600">{m.vencidas ?? 0} vencida(s)</p>
          <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {m.proximas_7_dias ?? 0} nos próximos 7 dias
          </p>
          <Link to="/app/plano-manutencao" className="text-sm text-blue-600 hover:underline mt-3 inline-block">
            Ver plano de manutenção
          </Link>
        </div>

        <div className="bg-white border rounded-xl p-5">
          <h2 className="font-semibold flex items-center gap-2 text-gray-900">
            <Cog className="h-5 w-5 text-gray-500" />
            Obsolescência (≥ 85% vida útil)
          </h2>
          <ul className="mt-3 space-y-2 text-sm">
            {(data?.obsolescencia_proxima || []).length === 0 ? (
              <li className="text-gray-500">Nenhum equipamento próximo do fim da vida útil.</li>
            ) : (
              data.obsolescencia_proxima.map((o) => (
                <li key={o.id} className="flex justify-between border-b pb-1">
                  <span>{o.apelido}</span>
                  <span className="font-medium text-amber-700">{o.percentual_vida}%</span>
                </li>
              ))
            )}
          </ul>
          <Link to="/app/equipamentos" className="text-sm text-blue-600 hover:underline mt-3 inline-block">
            Gerenciar equipamentos
          </Link>
        </div>
      </div>

      {(utilizacao?.ranking || []).length > 0 && (
        <div className="bg-white border rounded-xl p-5">
          <h2 className="font-semibold text-gray-900 mb-3">Utilização média (produção recente)</h2>
          <p className="text-xs text-gray-500 mb-4">
            Com base no peso da ordem e no tempo da etapa vs. capacidade nominal do equipamento.
          </p>
          <ul className="space-y-2">
            {utilizacao.ranking.slice(0, 8).map((r) => (
              <li key={r.equipamento_id} className="flex justify-between text-sm border-b pb-2">
                <span>{r.equipamento_apelido}</span>
                <span
                  className={`font-semibold ${
                    (r.media_utilizacao_pct || 0) > 100 ? 'text-red-600' : 'text-blue-700'
                  }`}
                >
                  {r.media_utilizacao_pct != null ? `${r.media_utilizacao_pct}%` : '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default EquipamentosPainelPage;
